/* eslint-disable camelcase */
import get from 'lodash/get';
import find from 'lodash/find';
import pick from 'lodash/pick';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';
import cond from 'lodash/cond';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import flow from 'lodash/flow';
import isInteger from 'lodash/isInteger';
import isPlainObject from 'lodash/isPlainObject';
import { decamelizeKeys, camelize, decamelize } from 'humps';
import { warn } from 'utils/console';
import Logger from 'observability/Logger';
import walkNestedData from 'common/utils/forms/walkNestedData';
import { INPUT_TYPE_CONFIGURATIONS } from 'common/compounds/FormBuilder/constants/bootstrapKeys';
import { isTemporaryId } from 'common/compounds/FormBuilder/utils/formValidations';
import { LOGIC_PROPERTY_LIMIT, VALUE_TYPE_STRING, VALUE_TYPE_NUMBER } from 'common/entities/formLogicRule/constants';
import uuid from 'utils/id';
import {
    ENTITIES_KEY_NODE,
    NODE_TYPE_QUESTION,
    NODE_TYPE_GROUP,
    NODE_TYPE_ROOT,
    NODE_TYPE_TICKET_SELECTION,
    NODE_TYPE_TICKET_SELECTION_DECAMELIZED,
} from '../formNode';
import {
    DATE_OPTION,
    DATE_OPTION_DURATION,
    DATE_OPTION_DATES,
    DATE_MAX,
    DATE_MIN,
    DATE_DURATION_VALUE,
    DATE_DURATION_UNIT,
} from '../formNode/nodeConfig';
import { ENTITIES_KEY_FORM_LOGIC_RULE } from '../formLogicRule';
import { NODE_NAME_DATE_RANGE_START_DATE, NODE_NAME_DATE_RANGE_END_DATE } from '../formNode/nodeNames';

const LOG = new Logger('form-utils');

/**
 * @param {*} value
 */
function isTypeofNumber(value) {
    return typeof value === 'number';
}

/**
 * @param {string} value
 */
function trimIfString(value) {
    return typeof value === 'string' ? value.trim() : value;
}

/**
 * casts all IDs that are numbers into strings. Helps keep front-end code IDs of the same type.
 * @param {Object} formNode
 * @param {Object}
 */
function castIdsToStrings(formNode) {
    const result = { ...formNode };

    if (isTypeofNumber(result.id)) {
        result.id = `${result.id}`;
    }

    if (result.logic) {
        result.logic = result.logic.map((originalLogic) => {
            const logic = { ...originalLogic };

            if (isTypeofNumber(logic.target)) {
                logic.target = `${logic.target}`;
            }

            if (
                isTypeofNumber(logic.operand_left) &&
                // @TODO, use another PR I have on the works to read from bootstrap.
                (logic.operand_left_type === 'question' ||
                    logic.operand_left_type === 'group' ||
                    logic.operand_left_type === 'form')
            ) {
                logic.operand_left = `${logic.operand_left}`;
            }

            if (
                isTypeofNumber(logic.operand_right) &&
                // @TODO, use another PR I have on the works to read from bootstrap.
                (logic.operand_right_type === 'question' ||
                    logic.operand_right_type === 'group' ||
                    logic.operand_right_type === 'form')
            ) {
                logic.operand_right = `${logic.operand_right}`;
            }

            return logic;
        });
    }

    return result;
}

/**
 * @param {Object} formNode
 * @return {Object}
 */
function camelizeInputType(formNode) {
    let updatedNode;
    if (formNode.input_type) {
        updatedNode = {
            ...formNode,
            input_type: camelize(formNode.input_type),
        };
    }
    return updatedNode || formNode;
}

/**
 * Back-end likes input type to be snake case. so "splashMultiselect" -> 'splash_multiselect
 *
 * @param {Object} formNode
 * @return {Object}
 */
function decamelizeInputType(formNode) {
    let updatedNode;
    if (formNode.inputType) {
        updatedNode = {
            ...formNode,
            inputType: decamelize(formNode.inputType),
        };
    }
    return updatedNode || formNode;
}

/**
 * @param {string} nodeType
 * @return {string}
 */
export function camelizeNodeTypeByTicketType(nodeType) {
    return nodeType === NODE_TYPE_TICKET_SELECTION_DECAMELIZED ? camelize(nodeType) : nodeType;
}

/**
 * @param {string} nodeType
 * @return {string}
 */
export function decamelizeNodeTypeByTicketType(nodeType) {
    return nodeType === NODE_TYPE_TICKET_SELECTION ? decamelize(nodeType) : nodeType;
}

/**
 * Removes the node.config keys that don't apply to the given node. Back-end would reject a save
 * if front-end passed invalid keys in there.
 *
 * @param {Object} formNode
 * @param {Object} inputTypeConfigurations - comes from bootstrap data.
 * @return {Object}
 */
function removeInvalidConfigKeys(formNode, inputTypeConfigurations) {
    let updatedNode = formNode;

    if (updatedNode.config) {
        updatedNode = { ...formNode };
        updatedNode.config = pick(
            updatedNode.config,
            Object.keys(inputTypeConfigurations[updatedNode.inputType] || {}),
        );
    }
    return updatedNode;
}

/**
 * Transforms API response form descriptor into the data structure front-end handles.
 *
 * @param  {Object} payloadFormData - form descriptor, with keys id and root among others.
 * @return {Object}
 */
export function transformInputPayloadFormData(payloadFormData) {
    const formData = { ...payloadFormData };

    if (isTypeofNumber(formData.id)) {
        formData.id = `${formData.id}`;
    }

    // Adding a default empty style configuration. Keys are still snake_case here.
    if (!isPlainObject(formData.style_configuration)) {
        formData.style_configuration = {};
    }

    // Adding default empty error messages. Keys are still snake_case here.
    if (!isPlainObject(formData.error_messages)) {
        formData.error_messages = {};
    }

    // Adding default empty form library form. Keys are still snake_case here.
    if (!isPlainObject(formData.form_library_form)) {
        formData.form_library_form = null;
    }

    // Adding default empty event form. Keys are still snake_case here.
    if (!isPlainObject(formData.event_form)) {
        formData.event_form = null;
    }

    if (formData.root) {
        formData.root = walkNestedData(formData.root || {}, (formNode) =>
            flow(
                (transformedNode) => castIdsToStrings(transformedNode),
                (transformedNode) => camelizeInputType(transformedNode),
            )(formNode),
        );
    } else {
        warn('FormBuilder', 'ID transformation - no formData.root, function might be receiving wrong structure.');
    }

    return formData;
}

export const replaceFormDataIdsWithNewUUIDs = (formNode, rootId) => {
    const result = { ...formNode };
    const newIds = {};

    if (!isTemporaryId(result.id)) {
        const newUUID = result.type === NODE_TYPE_ROOT ? rootId : `_${uuid()}`;

        const oldId = result.id;
        newIds[oldId] = newUUID;
        result.id = newUUID;
    }

    return { result, newIds };
};

export const replaceFormLogicIdsWithNewUUIDs = (root, ids) =>
    root.map((logic) => ({
        ...logic,
        target: ids[logic.target] ? ids[logic.target] : logic.target,
        operand_left: ids[logic.operand_left] ? ids[logic.operand_left] : logic.operand_left,
        // these cases are not possible using the current UI
        operand_right: ids[logic.operand_right] ? ids[logic.operand_right] : logic.operand_right,
        default_value: ids[logic.default_value] ? ids[logic.default_value] : logic.default_value,
    }));

export const replaceFieldNodeIdsWithNewUUIDs = (root, ids) =>
    root.map((node) =>
        node.fields
            ? {
                  ...node,
                  fields: Object.keys(node.fields).reduce((newFields, fieldName) => {
                      const field = node.fields[fieldName];

                      return {
                          ...newFields,
                          [fieldName]: ids[field.prepopulate_node_id]
                              ? {
                                    ...field,
                                    prepopulate_node_id: ids[field.prepopulate_node_id],
                                }
                              : field,
                      };
                  }, {}),
              }
            : node,
    );

/**
 * Transforms front-end data format into the form descriptor API accepts.
 *
 * @param  {Object} payloadFormData - form descriptor, with keys id and root among others.
 * @param  {Object} params.inputTypeConfigurations - comes from bootstrap data.
 * @return {Object}
 */
export function transformOutputPayloadFormData(payloadFormData, bootstrap) {
    const hasStyleConfiguration = Object.keys((payloadFormData && payloadFormData.styleConfiguration) || {}).length > 0;

    // Fully removing the key `styleConfiguration` in case the form doesn't have any styling info
    // so that we don't pollute production forms data in the DB with this key while the flag is off.
    // Removing `eventForm` — it is read-only metadata from the BE and must not be written back.
    const formData = omit(
        !hasStyleConfiguration ? omit(payloadFormData, 'styleConfiguration') : payloadFormData,
        'eventForm',
    );

    const inputTypeConfigurations = bootstrap[INPUT_TYPE_CONFIGURATIONS];

    if (!formData.root) {
        warn('FormBuilder', 'ID transformation - no formData.root, function might be receiving wrong structure.');
        return formData;
    }

    formData.root = walkNestedData(formData.root || {}, (formNode) =>
        flow(
            (transformedNode) => removeInvalidConfigKeys(transformedNode, inputTypeConfigurations),
            (transformedNode) => decamelizeInputType(transformedNode),
        )(formNode),
    );

    const decamelizedFormData = decamelizeKeys(formData);

    return decamelizedFormData;
}

/**
 * Transforms a question descriptor to be sent to the API to be saved.
 *
 * @param  {Object} question
 * @return {Object}
 */
export const transformOutputQuestionData = flow(
    (question) => decamelizeInputType(question),
    (transformedQuestion) => decamelizeKeys(transformedQuestion),
);

/**
 * Replaces logic rules for response limits in multi choice questions for the key `responseLimit`
 * in the option descriptor. The rules are removed.
 *
 * @param {object} normalizedData
 * @return {object} - new normalized data
 */
export function transformLogicRulesIntoResponseLimitValues(normalizedData) {
    const { entities } = normalizedData;

    if (!entities[ENTITIES_KEY_FORM_LOGIC_RULE]) {
        return normalizedData;
    }

    const nodes = entities[ENTITIES_KEY_NODE] || {};

    const limitLogicRules = Object.values(entities[ENTITIES_KEY_FORM_LOGIC_RULE]).filter(
        (logicRule) => logicRule.property === LOGIC_PROPERTY_LIMIT,
    );

    // Create object of updated nodes with `responseLimit` key in their options.
    const nodesWithLimit = {};
    limitLogicRules.forEach((logicRule) => {
        const targetNodeId = logicRule.target;
        const value = logicRule.defaultValue;
        const responseLimit = logicRule.operandLeft;

        const nodeOptions = get(nodes, `${targetNodeId}.config.options.entities`);

        // If no options found, maybe there's limit logic targeting the question, but the question
        // type was changed after and it doesn't support response limits.
        if (!nodeOptions) {
            return;
        }

        const option = find(nodeOptions, (nodeOption) => nodeOption.value === value);

        // If there is no option, maybe the value is not matching, so the response limit
        // won't be applicable
        if (!option) {
            const errorMessge = 'Corrupt data, the values in optionNode and logicRule do NOT match.';
            LOG.warn(errorMessge);
            return;
        }

        const previousNode = nodesWithLimit[targetNodeId] || nodes[targetNodeId];

        const updatedNode = {
            ...previousNode,
            config: {
                ...get(previousNode, 'config', {}),
                options: {
                    ...get(previousNode, 'config.options', {}),
                    entities: {
                        ...get(previousNode, 'config.options.entities', {}),
                        [option.id]: {
                            ...option,
                            responseLimit,
                        },
                    },
                },
            },
        };

        nodesWithLimit[targetNodeId] = updatedNode;
    });

    const updatedNodes = {
        ...nodes,
        ...nodesWithLimit,
    };

    // Remove logic references from root node.
    const limitLogicRulesById = keyBy(limitLogicRules, 'id');
    const updatedNodesWithoutLogicReferences = mapValues(updatedNodes, (node) => ({
        ...node,
        logic: (node.logic || []).filter((logicId) => !limitLogicRulesById[logicId]),
    }));

    return {
        ...normalizedData,
        entities: {
            ...normalizedData.entities,
            [ENTITIES_KEY_NODE]: updatedNodesWithoutLogicReferences,
            // Remove limit logic rules.
            [ENTITIES_KEY_FORM_LOGIC_RULE]: omitBy(
                entities[ENTITIES_KEY_FORM_LOGIC_RULE],
                (logicRule) => logicRule.property === LOGIC_PROPERTY_LIMIT,
            ),
        },
    };
}

function trimConfigOptions(entities) {
    return Object.keys(entities).reduce((acc, optionId) => {
        const option = entities[optionId];

        return {
            ...acc,
            [optionId]: {
                ...option,
                label: trimIfString(option.label),
                value: trimIfString(option.value),
            },
        };
    }, {});
}

function trimLabels(normalizedData) {
    const { entities } = normalizedData;

    const formNodes = Object.values(entities[ENTITIES_KEY_NODE] || {}).reduce((acc, formNode) => {
        const { label } = formNode;

        const config = get(formNode, 'config.options')
            ? {
                  ...formNode.config,
                  options: {
                      ...formNode.config.options,
                      entities: trimConfigOptions(formNode.config.options.entities),
                  },
              }
            : formNode.config;

        return {
            ...acc,
            [formNode.id]: {
                ...formNode,
                label: trimIfString(label),
                config,
            },
        };
    }, {});

    const logicRules = Object.values(entities[ENTITIES_KEY_FORM_LOGIC_RULE] || {}).reduce((acc, logicRule) => {
        const { operandRight, defaultValue } = logicRule;

        return {
            ...acc,
            [logicRule.id]: {
                ...logicRule,
                operandRight: trimIfString(operandRight),
                defaultValue: trimIfString(defaultValue),
            },
        };
    }, {});

    return {
        ...normalizedData,
        entities: {
            ...entities,
            [ENTITIES_KEY_FORM_LOGIC_RULE]: logicRules,
            [ENTITIES_KEY_NODE]: formNodes,
        },
    };
}

export const transformInputData = (normalizedData) =>
    flow(trimLabels, transformLogicRulesIntoResponseLimitValues)(normalizedData);

/**
 * Turns values of `responseLimits` in option descriptors into form logic rules, which is how
 * the back-end saves that information.
 *
 * @param {object} entities
 * @return {object} - new entities
 */
export function transformResponseLimitValuesIntoLogicRules(entities) {
    const newLimitLogicRules = Object.values(entities[ENTITIES_KEY_NODE]).reduce((memo, node) => {
        const optionIds = get(node, 'config.options.result', []);
        const options = optionIds.map((id) => get(node, `config.options.entities[${id}]`)).filter(Boolean);

        const optionsWithLimit = Object.values(options).filter((option) => option.responseLimit != null);

        const limitLogicRules = optionsWithLimit.map((option) => ({
            property: LOGIC_PROPERTY_LIMIT,
            target: node.id,
            targetType: node.type,

            id: uuid(),
            formId: node.formId,
            defaultValue: option.value,
            defaultValueType: VALUE_TYPE_STRING,
            operator: null,
            operandLeft: option.responseLimit,
            operandLeftType: VALUE_TYPE_NUMBER,
            operandRight: null,
            operandRightType: null,
        }));

        return [...memo, ...limitLogicRules];
    }, []);

    // Remove `responseLimit` from all options.
    const updatedNodes = mapValues(entities[ENTITIES_KEY_NODE], (node) => {
        const optionEntities = get(node, 'config.options.entities');
        if (!optionEntities) {
            return node;
        }
        const newOptionEntities = mapValues(optionEntities, ({ responseLimit, ...rest }) => ({
            ...rest,
        }));

        return {
            ...node,
            config: {
                ...node.config,
                options: {
                    ...node.config.options,
                    entities: newOptionEntities,
                },
            },
        };
    });

    // Update root node adding it the generated logic rules.
    const rootNode = Object.values(updatedNodes).find((node) => node.type === NODE_TYPE_ROOT);
    const updatedRootNode = {
        ...rootNode,
        logic: [...rootNode.logic, ...newLimitLogicRules.map((logicRule) => logicRule.id)],
    };

    return {
        ...entities,
        [ENTITIES_KEY_NODE]: {
            ...updatedNodes,
            [updatedRootNode.id]: updatedRootNode,
        },
        // Remove limit logic rules.
        [ENTITIES_KEY_FORM_LOGIC_RULE]: {
            ...entities[ENTITIES_KEY_FORM_LOGIC_RULE],
            ...keyBy(newLimitLogicRules, 'id'),
        },
    };
}

/**
 * If the operand passed represents a node, returns its id.
 * @param  {string} operandType
 * @param  {string} operand
 * @return {string|undefined}
 */
function getNodeId(operandType, operand) {
    return operandType === NODE_TYPE_QUESTION || operandType === NODE_TYPE_GROUP ? operand : undefined;
}

/**
 * Loops through all logic and removes logic rules that reference unexisting nodes.
 * Helps when bad rules are left hanging in the store.
 *
 * @param  {Object} entities
 * @return {Object} - updated entities
 */
export function removeUnreferencedLogicRules(entities) {
    if (!entities[ENTITIES_KEY_FORM_LOGIC_RULE]) {
        return entities;
    }

    const referencedLogicRuleIds = Object.keys(entities[ENTITIES_KEY_FORM_LOGIC_RULE]).filter((logicRuleId) => {
        const {
            target,
            targetType,
            defaultValue,
            defaultValueType,
            operandLeft,
            operandLeftType,
            operandRight,
            operandRightType,
        } = entities[ENTITIES_KEY_FORM_LOGIC_RULE][logicRuleId];

        const targetId = getNodeId(targetType, target);
        const defaultValueId = getNodeId(defaultValueType, defaultValue);
        const operandLeftId = getNodeId(operandLeftType, operandLeft);
        const operandRightId = getNodeId(operandRightType, operandRight);

        return (
            (!targetId || entities[ENTITIES_KEY_NODE][targetId]) &&
            (!defaultValueId || entities[ENTITIES_KEY_NODE][defaultValueId]) &&
            (!operandLeftId || entities[ENTITIES_KEY_NODE][operandLeftId]) &&
            (!operandRightId || entities[ENTITIES_KEY_NODE][operandRightId])
        );
    });

    return {
        ...entities,
        [ENTITIES_KEY_FORM_LOGIC_RULE]: pick(entities[ENTITIES_KEY_FORM_LOGIC_RULE], referencedLogicRuleIds),
    };
}

const getPurgedDateRangeEntities = (storeEntities, dateQuestions) => {
    const dateQuestionsPurged = dateQuestions.map((node) => {
        const isDates = get(node, ['config', DATE_OPTION]) === DATE_OPTION_DATES;
        const isDuration = get(node, ['config', DATE_OPTION]) === DATE_OPTION_DURATION;

        return {
            ...node,
            config: {
                ...get(node, ['config']),
                [DATE_MAX]: isDates ? get(node, ['config', DATE_MAX]) : null,
                [DATE_MIN]: isDates ? get(node, ['config', DATE_MIN]) : null,
                [DATE_DURATION_VALUE]: isDuration ? get(node, ['config', DATE_DURATION_VALUE]) : null,
                [DATE_DURATION_UNIT]: isDuration ? get(node, ['config', DATE_DURATION_UNIT]) : null,
            },
        };
    });

    return {
        ...storeEntities,
        formNodes: {
            ...storeEntities.formNodes,
            ...keyBy(dateQuestionsPurged, 'id'),
        },
    };
};

export const transformDateRange = (storeEntities) => {
    const formNodes = get(storeEntities, 'formNodes', {});
    const dateQuestions = Object.keys(formNodes)
        .filter((nodeId) => {
            const node = formNodes[nodeId];
            return node.name === NODE_NAME_DATE_RANGE_START_DATE || node.name === NODE_NAME_DATE_RANGE_END_DATE;
        })
        .map((nodeId) => formNodes[nodeId]);

    return dateQuestions.length > 0 ? getPurgedDateRangeEntities(storeEntities, dateQuestions) : storeEntities;
};

export const transformStyleConfiguration = (serializedData) => {
    const { button, close_button, form, form_fields } = serializedData?.style_configuration;

    const updatedStyleConfiguration = {
        ...serializedData,
        style_configuration: {
            ...serializedData?.style_configuration,
            button: {
                ...button,
                border: {
                    ...button?.border,
                    colors: {
                        ...button?.border?.colors,
                        hover: cond([
                            [() => isInteger(button?.border?.colors?.hover), () => button?.border?.colors?.hover],
                            [() => true, () => null],
                        ])(),
                        focus: cond([
                            [() => isInteger(button?.border?.colors?.focus), () => button?.border?.colors?.focus],
                            [() => isInteger(button?.border?.colors?.hover), () => button?.border?.colors?.hover],
                            [() => true, () => null],
                        ])(),
                    },
                },
            },
            close_button: {
                ...close_button,
                hover: cond([
                    [() => isInteger(close_button?.hover), () => close_button?.hover],
                    [() => true, () => null],
                ])(),
                focus: cond([
                    [() => isInteger(close_button?.focus), () => close_button?.focus],
                    [() => isInteger(close_button?.hover), () => close_button?.hover],
                    [() => true, () => null],
                ])(),
            },
            form: {
                ...form,
                links: {
                    ...form?.links,
                    hover: cond([
                        [() => isInteger(form?.links?.hover), () => form?.links.hover],
                        [() => true, () => null],
                    ])(),
                    focus: cond([
                        [() => isInteger(form?.links?.focus), () => form?.links?.focus],
                        [() => isInteger(form?.links?.hover), () => form?.links?.hover],
                        [() => true, () => null],
                    ])(),
                },
            },
            form_fields: {
                ...form_fields,
                border: {
                    ...form_fields?.border,
                    colors: {
                        ...form_fields?.border?.colors,
                        hover: cond([
                            [
                                () => isInteger(form_fields?.border?.colors?.hover),
                                () => form_fields?.border?.colors?.hover,
                            ],
                            [() => true, () => null],
                        ])(),
                        focus: cond([
                            [
                                () => isInteger(form_fields?.border?.colors?.focus),
                                () => form_fields?.border?.colors?.focus,
                            ],
                            [
                                () => isInteger(form_fields?.border?.colors?.hover),
                                () => form_fields?.border?.colors?.hover,
                            ],
                            [() => true, () => null],
                        ])(),
                    },
                },
            },
        },
    };
    return updatedStyleConfiguration;
};
